/**
 * EL ESTADO DE UNA VISITA — un solo sitio para los siete.
 *
 * El dato se guarda en inglés (`Pending`, `Entered`, `Exited`,
 * `AwaitingApproval`, `Denied`, `Revoked`, `Expired`), como el resto del
 * modelo, y aquí se traduce. Está centralizado por la misma razón que
 * `llegada.js`: el estado se pinta en la insignia compartida, en el historial,
 * en la búsqueda del guardia, en la lista del residente y en el pase del
 * visitante, y con la traducción repetida en cada pantalla ya pasó lo que
 * tenía que pasar — dos agentes de QA encontraron el mismo estado llamado de
 * tres formas: «Revoked» crudo en el historial, «Revocado» en la búsqueda y
 * «Revocada» en la insignia.
 *
 * **Una sola forma por estado, y en femenino**, porque el sujeto es «la
 * visita» / «la llegada». La insignia compartida ya hablaba así («Denegada»,
 * «Revocada», «Vencida») y es la que más pantallas alimentan: cambiar las
 * otras a su forma deja menos mentiras a medio camino.
 */

/**
 * Cada estado trae lo que necesitan las tres formas de pintarlo: el texto, el
 * icono, el nombre de variante que entiende `Badge` y el color suelto para
 * quien dibuja un borde o una línea en vez de una insignia.
 *
 * `color` y `variant` describen el mismo estado por duplicado a propósito: la
 * búsqueda tiñe el borde izquierdo de una tarjeta y no puede usar una
 * variante de `Badge`, y sacarlo de aquí es lo que impide que un estado se vea
 * rojo en una pantalla y gris en la otra.
 */
export const ESTADOS = {
  Pending: {
    etiqueta: "Pendiente",
    icono: "🕓",
    variant: "pending",
    color: "var(--warning)",
  },
  Entered: {
    etiqueta: "Entró",
    icono: "✅",
    variant: "entered",
    color: "var(--success)",
  },
  Exited: {
    etiqueta: "Salió",
    icono: "🚪",
    variant: "exited",
    color: "var(--text-tertiary)",
  },
  /* «Esperando autorización», sin el «su» que tenía la insignia: esto también
     lo lee el guardia en la caseta, y él no es quien autoriza. */
  AwaitingApproval: {
    etiqueta: "Esperando autorización",
    icono: "🔔",
    variant: "warning",
    color: "var(--warning)",
  },
  Denied: {
    etiqueta: "Denegada",
    icono: "⛔",
    variant: "danger",
    color: "var(--danger)",
  },
  Revoked: {
    etiqueta: "Revocada",
    icono: "🚫",
    variant: "danger",
    color: "var(--danger)",
  },
  Expired: {
    etiqueta: "Vencida",
    icono: "⌛",
    variant: "default",
    color: "var(--text-tertiary)",
  },
};

/**
 * El estado de una visita, con respaldo.
 *
 * A diferencia de `llegada.js`, aquí el respaldo **no** es uno de los siete:
 * un estado desconocido se enseña TAL CUAL. Caer a «Pendiente» era
 * exactamente el fallo que tumbó esto la primera vez —un pase revocado se veía
 * idéntico a uno válido—, y en un sistema que controla quién entra a las casas
 * una etiqueta que miente sobre el estado de un pase no es un detalle de
 * presentación. El valor crudo es feo, pero se ve y se corrige.
 */
export const estadoDe = (status) =>
  ESTADOS[status] || {
    etiqueta: String(status || "Sin estado"),
    icono: "❔",
    variant: "default",
    color: "var(--text-tertiary)",
  };

/** El estado en palabras, para intercalarlo en una línea de texto. */
export const etiquetaEstado = (status) => estadoDe(status).etiqueta;

/**
 * TIPO DE VISITANTE — los mismos trece que ofrece el catálogo del servidor.
 *
 * El catálogo manda cuando la pantalla lo tiene a mano (un formulario que
 * ofrece tipos que la residencial no usa es una trampa para el residente),
 * pero las pantallas de consulta sólo reciben el identificador guardado en la
 * visita y necesitan traducirlo sin pedir nada. Sin esta tabla salía
 * `empleado_domestico` a la cara del guardia, que es lo que reportó QA.
 */
export const TIPOS_VISITANTE = {
  invitado: "Invitado",
  familiar: "Familiar",
  amigo: "Amigo",
  empleado_domestico: "Empleado doméstico",
  ninera: "Niñera",
  jardinero: "Jardinero",
  tecnico: "Técnico",
  proveedor: "Proveedor",
  delivery: "Delivery",
  transporte: "Uber / transporte",
  contratista: "Contratista",
  mensajero: "Mensajero",
  otro: "Otro",
};

/**
 * El tipo de visitante en palabras.
 *
 * Un tipo que no esté en la tabla se deja legible en vez de crudo: se cambian
 * los guiones bajos por espacios y se levanta la primera letra. No queda
 * perfecto —se pierden las tildes que nadie puso en el identificador— pero
 * «Guardia privado» se lee, y `guardia_privado` no. Devolver el identificador
 * tal cual era el defecto que esto arregla; devolver `undefined` sería peor,
 * porque la pantalla pintaría un hueco.
 */
export const etiquetaTipoVisitante = (tipo) => {
  if (TIPOS_VISITANTE[tipo]) return TIPOS_VISITANTE[tipo];
  if (!tipo) return "";
  const texto = String(tipo).replace(/_/g, " ");
  return texto.charAt(0).toUpperCase() + texto.slice(1);
};
